import { RefObject, useEffect, useRef, useState } from "react";

/** Distance (px) from the bottom within which the conversation is considered pinned to the latest message. */
const PIN_THRESHOLD_PX = 24;

interface AutoScrollInput {
  /** Number of messages; a change signals new content to scroll to. */
  readonly messageCount: number;
  /** Whether the thinking indicator is showing; its appearance should also scroll. */
  readonly isLoading: boolean;
  /** Whether the error state is showing; its appearance should also scroll. */
  readonly hasError: boolean;
}

interface AutoScrollResult {
  readonly scrollRegionRef: RefObject<HTMLDivElement>;
  readonly isScrolledUp: boolean;
  readonly handleScroll: () => void;
  readonly scrollToLatest: () => void;
}

/**
 * Keeps the conversation pinned to the newest content. Auto-scrolls on new
 * messages, loading, and error changes only while the user is at the bottom;
 * suspends when they scroll up to read history and resumes via {@link scrollToLatest}.
 */
export const useAutoScroll = ({ messageCount, isLoading, hasError }: AutoScrollInput): AutoScrollResult => {
  const scrollRegionRef = useRef<HTMLDivElement>(null);
  /* True while scrolled to the bottom, so new messages only auto-scroll when pinned there. */
  const isPinnedToBottomRef = useRef(true);
  /* Pending rAF id used to batch scroll events into one measurement per frame. */
  const scrollFrameRef = useRef<number | null>(null);
  const [isScrolledUp, setIsScrolledUp] = useState(false);

  useEffect(() => {
    /* Follow new content to the bottom, unless the user scrolled up to read history. */
    if (!isPinnedToBottomRef.current) return;
    const region = scrollRegionRef.current;
    if (!region) return;
    region.scrollTo?.({ top: region.scrollHeight });
  }, [messageCount, isLoading, hasError]);

  useEffect(() => {
    /* Cancel a queued frame if we unmount mid-scroll. */
    return () => {
      if (scrollFrameRef.current !== null) cancelAnimationFrame(scrollFrameRef.current);
    };
  }, []);

  const handleScroll = () => {
    /* Batch rapid scroll events into one measurement per frame. */
    if (scrollFrameRef.current !== null) return;
    scrollFrameRef.current = requestAnimationFrame(() => {
      scrollFrameRef.current = null;
      const region = scrollRegionRef.current;
      if (!region) return;
      const distanceFromBottom = region.scrollHeight - region.scrollTop - region.clientHeight;
      isPinnedToBottomRef.current = distanceFromBottom <= PIN_THRESHOLD_PX;
      setIsScrolledUp(distanceFromBottom > PIN_THRESHOLD_PX);
    });
  };

  const scrollToLatest = () => {
    const region = scrollRegionRef.current;
    if (!region) return;
    isPinnedToBottomRef.current = true;
    region.scrollTo?.({ top: region.scrollHeight, behavior: "smooth" });
  };

  return { scrollRegionRef, isScrolledUp, handleScroll, scrollToLatest };
};
